import { FC } from 'react';
import { Avatar as MuiAvatar, Tooltip, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { AvatarSize } from './helpers';

interface Props {
  label: string;
  size?: AvatarSize;
  color?: string;
  backgroundColor?: string;
  tooltip?: string;
  className?: string;
}

const useStyles = createUseStyles<string, { size: number; backgroundColor?: string }>(
  (theme: Theme) => ({
    avatar: {
      width: ({ size }) => size,
      height: ({ size }) => size,
      fontSize: ({ size }) => size / 2,
      backgroundColor: ({ backgroundColor }) => backgroundColor || theme.palette.gray,
      color: theme.palette.common.white,
    },
  })
);

const getSizeInPixels = (size: AvatarSize): number => {
  if (typeof size === 'number') {
    return size;
  }

  switch (size) {
    case 'x-small':
      return 24;
    case 'small':
      return 20;
    case 'medium':
      return 32;
    case 'large':
      return 40;
    default:
      return 24;
  }
};

const Avatar: FC<Props> = ({
  label,
  size = 'x-small',
  color,
  backgroundColor,
  tooltip,
  className,
}) => {
  const sizeInPixels = getSizeInPixels(size);
  const avatarColor = color || backgroundColor;

  const styles = useStyles({ size: sizeInPixels, backgroundColor: avatarColor });
  const compClassName = classNames(styles.avatar, className);

  const avatarElement = <MuiAvatar className={compClassName}>{label}</MuiAvatar>;

  if (tooltip) {
    return <Tooltip title={tooltip}>{avatarElement}</Tooltip>;
  }

  return avatarElement;
};

export default Avatar;
